/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:53:06]
Permissions to update outside the main branch: [false]
*/

import {
    Button,
    DropDown,
    Form,
    Modal,
    Tab,
    unmaskNumberString,
    useErrorState,
} from '@finbeat/react-toolkit';
import { AxiosError } from 'axios';
import { getSelectList } from 'axios/dictionaryApi';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { usePingCrmApiCallback } from 'hooks/usePingCrmApiCallback';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { Role } from 'models/api/roles';
import { UserItem } from 'models/api/users';
import { Dispatch, Key, SetStateAction, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { ModalBody, ModalFooter, ModalHeader, Nav, NavItem, NavLink, TabContent } from 'reactstrap';
import 'scss/Main.scss';
import { useUserWorkplaceState } from '../Users';
import '../Users.scss';
import MainTab from './MainTab';
import RolesTab from './RolesTab';
import TradeOrgTab from './TradeOrgTab';
import BrandCodeTab from './BrandCodeTab';

type UserChangeModalProps = {
    isOpen?: boolean;
    toggle: Dispatch<SetStateAction<boolean>>;
};

const defaultUserItem: UserItem = {
    id: '',
    userName: '',
    firstName: '',
    lastName: '',
    middleName: '',
    phoneNumber: '',
    email: '',
    password: '',
    roles: [],
    brandCode: '',
    crmRole: '',
    tradeOrgId: null,
    outletIds: [],
    isTechnicalUser: false,
    isExternalUser: false,
};

const UserChangeModal = ({ isOpen = false, toggle }: UserChangeModalProps) => {
    const { t } = useTranslation();
    const { hasRole } = useAuth();
    const api = useApi();
    const formRef = useRef<HTMLFormElement>(null);
    const pingCrm = usePingCrmApiCallback();
    const { handleRequestError } = useErrorState();

    const [roles, setRoles] = useState<Role[]>([]);
    const [currentActiveTab, setCurrentActiveTab] = useState<string>('1');

    const toggleTab = (tab?: Key) => {
        if (currentActiveTab !== tab) setCurrentActiveTab(tab?.toString() ?? '');
    };

    const { currentItem, changeItemMutation, addItemMutation } = useUserWorkplaceState();

    const saveChanges = async (item: UserItem, noSyncWithCrm: boolean) => {
        const resItem = {
            userName: item.userName || '',
            firstName: item.firstName || '',
            lastName: item.lastName || '',
            middleName: item.middleName || '',
            phoneNumber: unmaskNumberString(item.phoneNumber || ''),
            email: item.email || '',
            roles: roles || [],
            brandCode: item.brandCode || '',
            crmRole: item.crmRole || '',
            noSyncWithCrm: noSyncWithCrm,
            tradeOrgId: item.tradeOrgId,
            outletIds: item.outletIds || [],
            isTechnicalUser: item.isTechnicalUser,
            isExternalUser: item.isExternalUser,
        };

        const onSuccess = () => toggle(false);

        item.id
            ? changeItemMutation.mutate(
                  { ...resItem, id: item.id || '', roleNames: resItem.roles.map((i) => i.name) },
                  {
                      onSuccess: onSuccess,
                  }
              )
            : addItemMutation.mutate(
                  {
                      ...resItem,
                      password: item.password || '',
                      roleNames: resItem.roles.map((i) => i.name),
                  },
                  {
                      onSuccess: onSuccess,
                  }
              );
    };

    const { data: crmRolesData, isFetching: loadingCrmRoles } = useQuery(
        'MoCrmRoles',
        () => getSelectList(api, ['MoCrmRoles']),
        {
            onError: (err: AxiosError<AxiosErrorResponse>) => {
                handleRequestError(err);
            },
        }
    );

    const crmRoles = useMemo<DropDown[]>(
        () => (crmRolesData && crmRolesData['MoCrmRoles']) ?? [],
        [crmRolesData]
    );

    const defaultValues = useMemo(
        () => ({
            ...defaultUserItem,
            crmRole: (crmRoles[0] && crmRoles[0].id?.toString()) ?? '',
            ...currentItem,
        }),
        [currentItem, crmRoles]
    );

    const hasTradeOrgRoles = useMemo(() => roles.some((r) => r.tradeOrgBindable), [roles]);
    const hasBrandCodeRoles = useMemo(() => roles.some((r) => r.brandCodeBindable), [roles]);

    const disabled = !(currentItem ? hasRole('accountsUpdate') : hasRole('accountsCreate'));

    return (
        <Modal size="lg" isOpen={isOpen} toggle={() => toggle(false)} scrollable>
            <ModalHeader toggle={() => toggle(false)}>
                {currentItem
                    ? `${t(
                          'BackOfficeEditUserModelName'
                      )}: ${currentItem?.lastName} ${currentItem?.firstName} ${
                          currentItem?.middleName ?? ''
                      }`
                    : t('BackOfficeNewUserModelName')}
            </ModalHeader>

            <ModalBody className="custom-scrollbar">
                <Nav tabs>
                    <NavItem>
                        <NavLink
                            className={currentActiveTab === '1' ? 'active' : ''}
                            onClick={() => toggleTab('1')}
                            role="button"
                        >
                            {t('BackOfficeInfo')}
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            className={currentActiveTab === '2' ? 'active' : ''}
                            onClick={() => toggleTab('2')}
                            role="button"
                        >
                            {t('BackOfficeRoles')}
                        </NavLink>
                    </NavItem>
                    {hasTradeOrgRoles && (
                        <NavItem>
                            <NavLink
                                className={currentActiveTab === '3' ? 'active' : ''}
                                onClick={() => toggleTab('3')}
                                role="button"
                            >
                                {t('BackOfficeTradeOrgs')}
                            </NavLink>
                        </NavItem>
                    )}
                    {hasBrandCodeRoles && (
                        <NavItem>
                            <NavLink
                                className={currentActiveTab === '4' ? 'active' : ''}
                                onClick={() => toggleTab('4')}
                                role="button"
                            >
                                {t('BackOfficeBrandCode')}
                            </NavLink>
                        </NavItem>
                    )}
                </Nav>

                <TabContent activeTab={currentActiveTab} className="user-modal__tab-content">
                    <Form<UserItem>
                        onSubmit={(data) => {
                            if (data?.isExternalUser) {
                                saveChanges(data, true);
                            } else {
                                pingCrm((props) =>
                                    props.available
                                        ? saveChanges(data, false)
                                        : props.resp && saveChanges(data, true)
                                );
                            }
                        }}
                        defaultValues={defaultValues}
                        innerRef={formRef}
                        clearAfterSubmit={false}
                    >
                        <MainTab
                            toggle={toggleTab}
                            disabled={disabled}
                            crmRoles={crmRoles}
                            loadingCrmRoles={loadingCrmRoles}
                        />
                        <Tab tabId="2" toggle={toggleTab} className="pt-3">
                            <RolesTab disabled={disabled} roles={roles} setRoles={setRoles} />
                        </Tab>
                        {hasTradeOrgRoles && (
                            <Tab tabId="3" toggle={toggleTab} className="pt-3">
                                <TradeOrgTab toggle={toggleTab} disabled={disabled} />
                            </Tab>
                        )}
                        {hasBrandCodeRoles && (
                            <Tab tabId="4" toggle={toggleTab} className="pt-3">
                                <BrandCodeTab toggle={toggleTab} disabled={disabled} />
                            </Tab>
                        )}
                    </Form>
                </TabContent>
            </ModalBody>

            <ModalFooter>
                <Button color="secondary" type="button" onClick={() => toggle(false)}>
                    <Trans i18nKey="BackOfficeCancel" />
                </Button>
                <Button
                    color="success"
                    disabled={disabled}
                    onClick={() => formRef.current?.requestSubmit()}
                >
                    <Trans i18nKey="BackOfficeSave" />
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default UserChangeModal;
